<script>
import { mapActions } from 'vuex';
import i18n from '@/plugins/i18n';
import DeleteWarning from './delete-warning.vue';

export default {
    components: {
        DeleteWarning,
    },



    props: {
        selectedCompanyId: {
            type: Number,
            required: true
        },

        userId: {
            type: Number,
            required: true,
        }
    },

    data() {
        return {
            timeoutId: null,
            facilityNumber: null,
            facilities: [],

            deletingItem: null,

            fallbackValue: null,
            
        }
    },

    computed: {
        headers() {
            return [
                { key: 'delete', title: '', flex: 1 },
                { key: 'name', title: i18n.t('companyProfile.modal4.name'), flex: 2 },
                { key: 'address', title: i18n.t('companyProfile.modal4.address'), flex: 4 },
                { key: 'phone', title: i18n.t('companyProfile.modal4.phone'), flex: 2 },
                { key: 'province', title: i18n.t('companyProfile.modal4.province'), flex: 2 },
            ];
        },
    },

    beforeDestroy() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    },

    async mounted() {
        await this.setupFacilities();
    },

    methods: {
        ...mapActions('company', [
            'getFacilitySlotNumber',
            'getFacilities',
            'createFacility',
            'updateFacility',
            'deleteFacility',
        ]),

        async setupFacilities() {
            this.getFacilitySlotNumber()
                .then(res => {
                    this.facilityNumber = res.data.facility_number;

                    const params = {
                        company_id: this.selectedCompanyId,
                        is_deleted: false
                    }

                    this.getFacilities(params)
                        .then(response => {
                            this.facilities = [...response.data];

                            const emptySlotCount = this.facilityNumber - this.facilities.length;

                            for (let i = 0; i < emptySlotCount; i++) {
                                this.facilities.push({
                                    facility_name: "",
                                    phone: "",
                                    address: ""
                                });
                            }

                            this.$emit("facility-count", this.facilities.length);
                        })
                })
        },

        async changeController(key, id, facility = null) {
            if (id) {
                await this._updateFacility(key, id);
            } else {
                this._createFacility(facility);
            }
        },

        async _createFacility(facility) {
            if (facility.facility_name == '' || facility.facility_name == null) {
                this.$notify('warning', i18n.t('companyProfile.modal4.provideName'), true);
                return;
            }

            const params = {
                company_id: this.selectedCompanyId,
                facility_name: facility.facility_name,
                address: facility.address,
                phone: facility.phone,
                created_by: this.userId
            }

            this.createFacility(params)
                .then(res => {
                    this.facilities.find(x => x.facility_name === facility.facility_name).facility_id = res.data.facility_id;
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async _updateFacility(key, id) {
            const facilityIdx = this.facilities.findIndex(x => x.facility_id == id);
            if (facilityIdx === -1) return;

            const facility = this.facilities[facilityIdx];

            if (facilityIdx == 0 && key !== 'province') {
                this.facilities[facilityIdx][`${key}`] = this.fallbackValue;
                this.$notify('error', i18n.t('companyProfile.modal4.contactUs'), true);
                return;
            }

            if (facility) {
                const params = {
                    facility_id: id,
                    body: {
                        [`${key}`]: facility[`${key}`]
                    }
                }

                this.updateFacility(params)
                    .then(async (res) => {
                        this.$notify('success', res.data.messageName);
                        await this.setFallbackValue(null);
                    })
                    .catch(async ({ response }) => {
                        this.facilities[facilityIdx][`${key}`] = this.fallbackValue;
                        this.$notify('error', response.data.messageName);
                        this.timeoutId = setTimeout(() => {
                            this.setFallbackValue(null);
                        }, 50);
                    })
            }
        },

        async _deleteFacility(id) {
            if (!id) {
                return;
            }

            const params = {
                facility_id: id
            }

            this.deleteFacility(params)
                .then(res => {
                    this.facilities.find(x => x.facility_id == id).facility_name = '';
                    this.facilities.find(x => x.facility_id == id).address = '';
                    this.facilities.find(x => x.facility_id == id).phone = '';
                    this.facilities.find(x => x.facility_id == id).facility_id = undefined;
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        async setFallbackValue(val) {
            this.fallbackValue = val;
        },

        setDeletingItem(val) {
            this.deletingItem = val;
        },

    }
}
</script>

<template>
    <div class="facilities" v-if="facilities.length >= 2">
        <div class="facility-headers">
            <div v-for="header in headers" :key="header.key" :style="{ flex: header.flex }">
                {{ header.title }}
            </div>
        </div>

        <div class="facility" v-for="(facility, i) in facilities" :key="i" :class="{ 'first-facility': i === 0 }">
            <div class="delete-facility-button">
                <button v-if="i !== 0" @click="setDeletingItem(facility.facility_id)"
                    @keydown.enter="setDeletingItem(facility.facility_id)" :disabled="!facility.facility_id">
                    <i class="fa-regular fa-trash-can"></i>
                </button>
                <div v-else class="cannot-edit">
                    {{ $t('companyProfile.modal4.cannotMain') }}
                </div>

                <delete-warning v-if="facility.facility_id == deletingItem && facility.facility_id != null"
                    @onClose="setDeletingItem(null)" @onOk="_deleteFacility(facility.facility_id)" />
            </div>

            <input type="text" :style="{ flex: headers[1].flex }" v-model="facility.facility_name" :disabled="i === 0"
                @change="changeController('facility_name', facility.facility_id, facility)"
                @focus="setFallbackValue(facility.facility_name)" @blur="setFallbackValue(null)"
                :placeholder="!facility.facility_name || facility.facility_name.length === 0 ? $t('companyProfile.requiredWarning') : ''"
            />

            <input type="text" :style="{ flex: headers[2].flex }" v-model="facility.address" :disabled="i === 0"
                @change="changeController('address', facility.facility_id, facility)"
                @focus="setFallbackValue(facility.address)" @blur="setFallbackValue(null)"
                :placeholder="!facility.address || facility.address.length === 0 ? $t('companyProfile.requiredWarning') : ''"
            />

            <input type="text" :style="{ flex: headers[3].flex }" v-model="facility.phone" :disabled="i === 0"
                @change="changeController('phone', facility.facility_id, facility)"
                @focus="setFallbackValue(facility.phone)" @blur="setFallbackValue(null)"
                :placeholder="!facility.phone || facility.phone.length === 0 ? $t('companyProfile.requiredWarning') : ''"
            />

            <input type="text" :style="{ flex: headers[4].flex }" v-model="facility.province"
                @change="changeController('province', facility.facility_id, facility)"
                @focus="setFallbackValue(facility.province)" @blur="setFallbackValue(null)"
            />

        </div>
    </div>
</template>

<style lang='scss' scoped>
.facilities {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
}

.facility-headers {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    >div {
        text-align: left;
        margin: .5em;
        color: var(--sustainfinity-navy);
        font-weight: bold;
        font-size: 1.1em;
    }
}

.facility {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    align-content: flex-start;
    flex-wrap: wrap;

    >.delete-facility-button {
        position: relative;
        margin: .5em;
        flex: 1;
        display: flex;
        align-items: center;

        >.cannot-edit {
            font-size: .8em;
            font-style: italic;
            color: var(--invalid-input);
        }

        >button {
            display: flex;
            align-items: center;

            outline: 0;
            border: 0;
            background-color: transparent !important;

            &:focus {
                outline: thin solid lightgray !important;
            }

            >i {
                color: var(--invalid-input);
            }

            &:hover {
                >i {
                    color: var(--invalid-input--hover);
                }
            }

            &:disabled {
                pointer-events: none !important;

                >i {
                    color: gray;
                }
            }
        }

    }

    >input {
        text-align: left;
        margin: .5em;
        outline: 0;
        border: 0;
        border-bottom: thin solid lightgray;
        background-color: whitesmoke;

        transition: border-bottom 500ms;

        &:focus {
            border-bottom: thin solid black;
        }

        &:disabled {
            border-radius: .2em;
            background-color: rgb(221, 221, 221);
            cursor: not-allowed;
        }

        &::placeholder {
            color: var(--invalid-input);
        }
    }

    >.first-facility {
        border: 0 !important;
        outline: 0 !important;
    }
}
</style>